// ADDED: a read-only view of one friend's shared shelf - the collections they marked
// public, the models inside them, and their multipacks if they share those.
//
// Deliberately shows the models only. Quantities and collection totals are never sent by
// the server, so how many of something a friend owns, and what their shelf is worth,
// stay private even between friends.
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';

import axios, { getErrorMessage } from '../../api/axios';

export default function FriendProfile() {
    const { id } = useParams();
    const [profile, setProfile] = useState(null);
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(true);

    // Which collection is open, and the models inside it
    const [openCollection, setOpenCollection] = useState(null);
    const [cars, setCars] = useState([]);
    const [isCarsLoading, setIsCarsLoading] = useState(false);

    useEffect(() => {
        setIsLoading(true);
        axios.get(`/friends/${id}/profile`)
            .then((response) => { setProfile(response.data); setError(''); })
            // A 404 here means "not your friend" as well as "no such person" - the server
            // answers both the same way on purpose, and so does this message.
            .catch((err) => setError(getErrorMessage(err, 'That collector is not on your friends list.')))
            .finally(() => setIsLoading(false));
    }, [id]);

    const openOne = (collection) => {
        // Clicking the open one closes it again
        if (openCollection?.id === collection.id) { setOpenCollection(null); setCars([]); return; }

        setOpenCollection(collection);
        setIsCarsLoading(true);
        axios.get(`/friends/${id}/collections/${collection.id}`)
            .then((response) => setCars(response.data.collection.cars))
            .catch(() => setCars([]))
            .finally(() => setIsCarsLoading(false));
    };

    return (
        <>
        <section className="cv-page pt-8 pb-6 px-4 sm:px-8">
            <div className="max-w-5xl mx-auto space-y-2">
                <Link to="/friends" className="text-sm cv-accent underline">&larr; Back to friends</Link>
                {profile &&
                    <div className="text-center space-y-2 pt-2">
                        <p className="cv-eyebrow">Shared with you</p>
                        <h1 className="cv-display text-4xl sm:text-5xl break-words">{profile.user.username}</h1>
                        <p className="text-sm cv-muted">{profile.user.name}</p>
                    </div>
                }
            </div>
        </section>

        <section className="cv-page px-4 sm:px-8 pb-10 max-w-5xl mx-auto w-full space-y-5">
            {isLoading && <p className="text-center cv-muted py-10">Loading...</p>}
            {error && <p className="cv-alert-error text-center">{error}</p>}

            {profile && profile.collections.length === 0 &&
                <div className="cv-card border-dashed p-8 text-center space-y-2">
                    <p className="cv-strong">Nothing shared yet.</p>
                    <p className="text-sm cv-muted">
                        {profile.user.username} has not marked any collection public.
                    </p>
                </div>
            }

            {/* Their public collections, each one expanding to its models */}
            {profile && profile.collections.map((collection) => (
                <div key={collection.id} className="cv-card overflow-hidden">
                    <button
                        type="button"
                        onClick={() => openOne(collection)}
                        aria-expanded={openCollection?.id === collection.id}
                        className="w-full flex items-center justify-between gap-3 px-4 py-4 text-left">
                        <div className="min-w-0">
                            <h2 className="cv-title text-lg truncate">{collection.collectionName}</h2>
                            <p className="text-xs cv-muted">
                                {collection._count.cars} {collection._count.cars === 1 ? 'model' : 'models'}
                            </p>
                        </div>
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"
                             className={`w-5 h-5 cv-muted shrink-0 transition-transform duration-300 ${openCollection?.id === collection.id ? 'rotate-180' : ''}`}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
                        </svg>
                    </button>

                    {openCollection?.id === collection.id &&
                        <div className="px-4 pb-4 animate-fade-up" style={{ borderTop: '1px solid var(--border-subtle)' }}>
                            {isCarsLoading && <p className="text-sm cv-muted py-4 text-center">Loading models...</p>}
                            {!isCarsLoading && cars.length === 0 &&
                                <p className="text-sm cv-muted py-4 text-center">This collection is empty.</p>
                            }
                            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 pt-4">
                                {cars.map((car) => (
                                    <div key={car.id} className="cv-well rounded-xl overflow-hidden">
                                        <div className="w-full h-28 flex items-center justify-center overflow-hidden">
                                            {/* MODIFIED: the ~20KB preview. Lists no longer receive the full photo, which */}
                                            {/* pushed a page over the database's 5MB response limit. */}
                                            {car.imageThumb
                                                ? <img src={car.imageThumb} alt={car.name} className="w-full h-full object-contain" />
                                                : <span className="text-xs cv-muted">No image</span>}
                                        </div>
                                        <div className="p-2">
                                            <p className="text-xs cv-strong break-words leading-snug">{car.name}</p>
                                            <p className="text-[0.65rem] cv-muted truncate">{car.brand} {car.model} &middot; {car.datereleased}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    }
                </div>
            ))}

            {/* Their packs, shown only when they have the switch on */}
            {profile && profile.multipacksPublic && profile.multipacks.length > 0 &&
                <div className="cv-card overflow-hidden">
                    <div className="px-4 pt-4 pb-3">
                        <h2 className="cv-display text-lg">Multipacks</h2>
                        <p className="text-xs cv-muted">{profile.multipacks.length} shared</p>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 px-4 pb-4">
                        {profile.multipacks.map((pack) => (
                            <div key={pack.id} className="cv-well rounded-xl overflow-hidden">
                                <div className="w-full h-28 flex items-center justify-center overflow-hidden">
                                    {/* MODIFIED: the ~20KB preview. Lists no longer receive the full photo, which */}
                                    {/* pushed a page over the database's 5MB response limit. */}
                                    {pack.imageThumb
                                        ? <img src={pack.imageThumb} alt={pack.name} className="w-full h-full object-contain" />
                                        : <span className="text-xs cv-muted">No image</span>}
                                </div>
                                <div className="p-2">
                                    <p className="text-xs cv-strong break-words leading-snug">{pack.name}</p>
                                    <p className="text-[0.65rem] cv-muted truncate">{pack.pieceCount}-pack &middot; {pack.datereleased}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            }
        </section>
        </>
    );
}
